import React, { createContext, useContext, useState, useEffect } from 'react';
import { PropertyCreatorRequest, UserProfile } from '../types/user';
import { useAuth } from './AuthContext';
import { toast } from 'react-hot-toast';

interface PropertyCreatorContextType {
  userProfile: UserProfile | null;
  isPropertyCreator: boolean;
  isApproved: boolean;
  submitCreatorRequest: (request: Omit<PropertyCreatorRequest, 'id' | 'userId' | 'userEmail' | 'userName' | 'submittedAt' | 'status'>) => Promise<void>;
  checkCreatorStatus: () => Promise<void>;
}

const PropertyCreatorContext = createContext<PropertyCreatorContextType | undefined>(undefined);

export function PropertyCreatorProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    if (user) {
      loadUserProfile();
    } else {
      setUserProfile(null);
    }
  }, [user]);

  const loadUserProfile = async () => {
    if (!user) return;

    try {
      const savedProfile = localStorage.getItem(`user_profile_${user.uid}`);
      if (savedProfile) {
        setUserProfile(JSON.parse(savedProfile));
      } else {
        // Create default profile
        const defaultProfile: UserProfile = {
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          isPropertyCreator: false,
          isApproved: false,
          createdAt: new Date().toISOString()
        };
        setUserProfile(defaultProfile);
        localStorage.setItem(`user_profile_${user.uid}`, JSON.stringify(defaultProfile));
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
    }
  };

  const submitCreatorRequest = async (requestData: Omit<PropertyCreatorRequest, 'id' | 'userId' | 'userEmail' | 'userName' | 'submittedAt' | 'status'>) => {
    if (!user || !userProfile) {
      toast.error('Please log in to submit a request');
      return;
    }

    try {
      const request: PropertyCreatorRequest = {
        ...requestData,
        id: Date.now().toString() + Math.random().toString(36).substring(2),
        userId: user.uid,
        userEmail: user.email || '',
        userName: user.displayName || user.email?.split('@')[0] || 'User',
        submittedAt: new Date().toISOString(),
        status: 'pending'
      };

      // Save request to localStorage (in real app, this would go to a database)
      const existingRequests = localStorage.getItem('property_creator_requests') || '[]';
      const requests = JSON.parse(existingRequests);
      requests.push(request);
      localStorage.setItem('property_creator_requests', JSON.stringify(requests));

      // Update user profile to show request is pending
      const updatedProfile = {
        ...userProfile,
        isPropertyCreator: true,
        isApproved: false
      };
      setUserProfile(updatedProfile);
      localStorage.setItem(`user_profile_${user.uid}`, JSON.stringify(updatedProfile));

      toast.success('Your request has been submitted for review. You will be notified once approved.');
    } catch (error) {
      console.error('Failed to submit creator request:', error);
      toast.error('Failed to submit request. Please try again.');
    }
  };

  const checkCreatorStatus = async () => {
    if (!user) return;
    await loadUserProfile();
  };

  const isPropertyCreator = userProfile?.isPropertyCreator || false;
  const isApproved = userProfile?.isApproved || false;

  return (
    <PropertyCreatorContext.Provider value={{
      userProfile,
      isPropertyCreator,
      isApproved,
      submitCreatorRequest,
      checkCreatorStatus
    }}>
      {children}
    </PropertyCreatorContext.Provider>
  );
}

export function usePropertyCreator() {
  const context = useContext(PropertyCreatorContext);
  if (!context) {
    throw new Error('usePropertyCreator must be used within a PropertyCreatorProvider');
  }
  return context;
}